import * as THREE from 'three';
import { buildWeapon, setChairState } from '../assets/WeaponBuilder';
import type { PlayerEntity } from '../entities/PlayerEntity';
import { COURT } from '../game/constants';
import { WEAPONS, WEAPON_TYPES, type WeaponType } from '../game/weapons';
import { disposeObject3D } from '../utils/dispose';

type WeaponState = 'inactive' | 'ground' | 'held' | 'flying';
interface ArenaWeapon {
  id: number;
  type: WeaponType;
  mesh: THREE.Group;
  ring: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  state: WeaponState;
  ownerId: string | null;
  throwerId: string | null;
  uses: number;
  lock: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
}

interface WeaponEvents {
  onPickup: (player: PlayerEntity, type: WeaponType) => void;
  onThrow: (player: PlayerEntity, type: WeaponType) => void;
  onHit: (attacker: PlayerEntity, target: PlayerEntity, type: WeaponType, direction: THREE.Vector3, broken: boolean, thrown: boolean) => void;
  onEnvironment: (position: THREE.Vector3, type: WeaponType, broken: boolean) => void;
}

export interface WeaponNetworkState {
  velocity?: number[];
  throwerId?: string | null;
  lock?: number;
  id: number;
  state: WeaponState;
  ownerId: string | null;
  uses: number;
  position: number[];
  rotation: number[];
}

export class ArenaWeapons {
  readonly group = new THREE.Group();
  private readonly objects: ArenaWeapon[] = [];
  private spawnTimer = 5;
  private readonly previous = new THREE.Vector3();
  private readonly segment = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();

  constructor(private readonly players: PlayerEntity[], private readonly rng: () => number, private readonly events: WeaponEvents) {
    for (let i = 0; i < WEAPON_TYPES.length * 2; i += 1) {
      const type = WEAPON_TYPES[i % WEAPON_TYPES.length];
      const mesh = buildWeapon(type);
      const ring = new THREE.Mesh(new THREE.RingGeometry(0.43, 0.49, 24), new THREE.MeshBasicMaterial({
        color: WEAPONS[type].color, transparent: true, opacity: 0.5, depthWrite: false,
      }));
      ring.rotation.x = -Math.PI / 2;
      mesh.visible = ring.visible = false;
      this.group.add(mesh, ring);
      this.objects.push({ id: i, type, mesh, ring, state: 'inactive', ownerId: null, throwerId: null,
        uses: WEAPONS[type].uses, lock: 0, position: new THREE.Vector3(), velocity: new THREE.Vector3() });
    }
  }

  reset(): void {
    this.clear();
    this.spawnTimer = 4 + this.rng() * 3;
    // Shuffle so every type can appear at kickoff despite the four-pickup cap.
    const types = [...WEAPON_TYPES];
    for (let i = types.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [types[i], types[j]] = [types[j], types[i]];
    }
    for (const type of types) this.spawn(type);
  }

  clear(): void {
    for (const object of this.objects) this.deactivate(object);
    this.spawnTimer = 5;
  }

  /** Used by development scenarios as well as the random spawner. */
  spawn(type?: WeaponType, position?: THREE.Vector3): boolean {
    if (this.objects.filter(object => object.state !== 'inactive').length >= 4) return false;
    const available = this.objects.filter(object => object.state === 'inactive' && (!type || object.type === type));
    const object = available[Math.floor(this.rng() * available.length)];
    if (!object) return false;
    const location = position?.clone() ?? new THREE.Vector3();
    if (!position) {
      let found = false;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        location.set((this.rng() * 2 - 1) * 11.5, 0, (this.rng() * 2 - 1) * 5.1);
        if (this.players.some(player => player.position.distanceTo(location) < 1.8)) continue;
        if (this.objects.some(other => other.state === 'ground' && other.position.distanceTo(location) < 2)) continue;
        found = true;
        break;
      }
      if (!found) return false;
    }
    object.uses = WEAPONS[object.type].uses;
    object.throwerId = null;
    object.position.copy(location);
    this.putOnGround(object, 0);
    return true;
  }

  private deactivate(object: ArenaWeapon): void {
    const owner = this.players.find(player => player.id === object.ownerId);
    if (owner) owner.weaponType = null;
    this.group.add(object.mesh);
    object.state = 'inactive';
    object.ownerId = object.throwerId = null;
    object.mesh.visible = object.ring.visible = false;
    object.velocity.set(0, 0, 0);
  }

  private putOnGround(object: ArenaWeapon, lock: number): void {
    const owner = this.players.find(player => player.id === object.ownerId);
    if (owner) owner.weaponType = null;
    this.group.add(object.mesh);
    object.ownerId = null;
    object.state = 'ground';
    object.lock = lock;
    object.position.y = object.type === 'chair' ? 0.19 : object.type === 'plank' ? 0.13 : 0.15;
    object.position.x = THREE.MathUtils.clamp(object.position.x, -COURT.halfLength + 1.75, COURT.halfLength - 1.75);
    object.position.z = THREE.MathUtils.clamp(object.position.z, -COURT.halfWidth + 1.75, COURT.halfWidth - 1.75);
    object.velocity.set(0, 0, 0);
    object.mesh.position.copy(object.position);
    object.mesh.rotation.set(0, this.rng() * Math.PI * 2, object.type === 'chair' ? 0 : Math.PI / 2);
    setChairState(object.mesh, object.uses, false);
    object.mesh.scale.setScalar(1);
    object.mesh.visible = object.ring.visible = true;
    object.ring.position.copy(object.position).setY(0.025);
  }

  heldBy(player: PlayerEntity): ArenaWeapon | undefined {
    return this.objects.find(object => object.state === 'held' && object.ownerId === player.id);
  }

  dropFrom(player: PlayerEntity): void {
    const object = this.heldBy(player);
    if (!object) return;
    object.position.copy(player.position);
    this.putOnGround(object, 0.85);
  }

  throwFrom(player: PlayerEntity): boolean {
    const object = this.heldBy(player);
    if (!object) return false;
    object.position.copy(player.position).addScaledVector(player.facing, 0.75);
    object.position.y = 1.05 + player.airborneY;
    this.group.add(object.mesh);
    object.mesh.scale.setScalar(1);
    object.mesh.position.copy(object.position);
    object.mesh.rotation.set(0, Math.atan2(player.facing.x, player.facing.z), Math.PI / 2);
    object.velocity.copy(player.facing).multiplyScalar(WEAPONS[object.type].throwSpeed);
    object.velocity.y = 3.2;
    object.state = 'flying';
    object.ownerId = null;
    object.throwerId = player.id;
    object.ring.visible = false;
    player.weaponType = null;
    this.events.onThrow(player, object.type);
    return true;
  }

  hitWith(player: PlayerEntity, target: PlayerEntity, direction: THREE.Vector3): boolean {
    const object = this.heldBy(player);
    if (!object) return false;
    const broken = --object.uses <= 0;
    setChairState(object.mesh, object.uses, true);
    this.events.onHit(player, target, object.type, direction, broken, false);
    if (broken) this.deactivate(object);
    return true;
  }

  consumeShot(player: PlayerEntity): boolean {
    const object = this.heldBy(player);
    if (!object || (object.type !== 'shotgun' && object.type !== 'bazooka')) return false;
    if (--object.uses <= 0) this.deactivate(object);
    return true;
  }

  networkState(): WeaponNetworkState[] {
    return this.objects.map(object => ({ id: object.id, state: object.state, ownerId: object.ownerId, uses: object.uses,
      velocity: object.velocity.toArray(), throwerId: object.throwerId, lock: object.lock,
      position: object.mesh.position.toArray(), rotation: [object.mesh.rotation.x, object.mesh.rotation.y, object.mesh.rotation.z] }));
  }

  applyNetworkState(states: WeaponNetworkState[]): void {
    for (const state of states) {
      const object = this.objects.find(candidate => candidate.id === state.id);
      if (!object) continue;
      object.state = state.state;
      object.ownerId = state.ownerId;
      object.velocity.fromArray(state.velocity ?? [0, 0, 0]);
      object.throwerId = state.throwerId ?? null;
      object.lock = state.lock ?? 0;
      object.uses = state.uses;
      setChairState(object.mesh, object.uses, state.state !== 'ground');
      const owner = this.players.find(player => player.id === state.ownerId);
      const parent = state.state === 'held' && owner ? owner.rig.armR.tip : this.group;
      if (object.mesh.parent !== parent) parent.add(object.mesh);
      object.mesh.position.fromArray(state.position);
      object.mesh.rotation.set(state.rotation[0], state.rotation[1], state.rotation[2]);
      object.mesh.visible = state.state !== 'inactive';
      object.ring.visible = state.state === 'ground';
      object.ring.position.copy(object.mesh.position).setY(0.025);
      if (state.state !== 'held') object.position.copy(object.mesh.position);
    }
  }

  update(delta: number, elapsed: number, allowSpawning = true): void {
    if (allowSpawning) this.spawnTimer -= delta;
    if (allowSpawning && this.spawnTimer <= 0) {
      this.spawn();
      this.spawnTimer = 5 + this.rng() * 4;
    }
    for (const object of this.objects) {
      object.lock = Math.max(0, object.lock - delta);
      if (object.state === 'held') {
        const owner = this.players.find(player => player.id === object.ownerId);
        if (!owner) this.deactivate(object);
        else if (owner.isDown || owner.state === 'celebrate' || owner.weaponType !== object.type) this.dropFrom(owner);
      } else if (object.state === 'flying') this.updateFlight(object, delta);
      else if (object.state === 'ground') {
        object.ring.material.opacity = 0.3 + Math.sin(elapsed * 4 + object.id) * 0.15;
        if (object.lock > 0) continue;
        for (const player of this.players) {
          if (player.weaponType || player.isGoalkeeper || player.isDown || !['idle', 'run', 'charge'].includes(player.state)) continue;
          if (Math.hypot(player.position.x - object.position.x, player.position.z - object.position.z) > 0.95) continue;
          object.state = 'held';
          object.ownerId = player.id;
          object.ring.visible = false;
          player.weaponType = object.type;
          player.rig.armR.tip.add(object.mesh);
          object.mesh.position.set(0, 0, 0);
          object.mesh.rotation.set(0, 0, 0);
          object.mesh.scale.setScalar(1);
          setChairState(object.mesh, object.uses, true);
          this.events.onPickup(player, object.type);
          break;
        }
      }
    }
  }

  private updateFlight(object: ArenaWeapon, delta: number): void {
    this.previous.copy(object.position);
    object.velocity.y -= 18 * delta;
    object.position.addScaledVector(object.velocity, delta);
    object.mesh.rotation.x += delta * 18;
    object.mesh.rotation.z += delta * 8;
    const thrower = this.players.find(player => player.id === object.throwerId);
    this.segment.copy(object.position).sub(this.previous);
    for (const target of this.players) {
      if (!thrower || target.team === thrower.team || target.isDown || target.state === 'celebrate') continue;
      this.offset.copy(target.position).setY(target.airborneY + 0.85).sub(this.previous);
      const t = THREE.MathUtils.clamp(this.offset.dot(this.segment) / (this.segment.lengthSq() || 1), 0, 1);
      if (this.offset.addScaledVector(this.segment, -t).lengthSq() > 0.85 ** 2) continue;
      const direction = object.velocity.clone().setY(0).normalize();
      const broken = --object.uses <= 0;
      setChairState(object.mesh, object.uses, true);
      this.events.onHit(thrower, target, object.type, direction, broken, true);
      if (broken) this.deactivate(object);
      else this.putOnGround(object, 0.85);
      return;
    }
    const wall = Math.abs(object.position.x) >= COURT.halfLength - 0.25 || Math.abs(object.position.z) >= COURT.halfWidth - 0.25;
    if (object.position.y <= 0.15 || wall) {
      object.position.x = THREE.MathUtils.clamp(object.position.x, -COURT.halfLength + 0.3, COURT.halfLength - 0.3);
      object.position.z = THREE.MathUtils.clamp(object.position.z, -COURT.halfWidth + 0.3, COURT.halfWidth - 0.3);
      const broken = --object.uses <= 0;
      setChairState(object.mesh, object.uses, true);
      this.events.onEnvironment(object.position.clone(), object.type, broken);
      if (broken) this.deactivate(object);
      else this.putOnGround(object, 0.6);
    } else object.mesh.position.copy(object.position);
  }

  readonly nearestPickup = (position: THREE.Vector3, maxDistance: number): THREE.Vector3 | null => {
    let nearest: THREE.Vector3 | null = null;
    let best = maxDistance * maxDistance;
    for (const object of this.objects) {
      if (object.state !== 'ground' || object.lock > 0) continue;
      const distance = object.position.distanceToSquared(position);
      if (distance < best) { best = distance; nearest = object.position; }
    }
    return nearest;
  };

  get diagnostics() {
    return this.objects.filter(object => object.state !== 'inactive').map(object => ({
      id: object.id, type: object.type, state: object.state, ownerId: object.ownerId, uses: object.uses,
      x: object.position.x, y: object.position.y, z: object.position.z,
    }));
  }

  dispose(): void {
    for (const object of this.objects) {
      this.deactivate(object);
      disposeObject3D(object.mesh);
      object.ring.geometry.dispose();
      object.ring.material.dispose();
    }
    this.group.clear();
  }
}
